/**
 * @file ossImage.js
 * @description 基于阿里云 OSS 图片处理参数，按展示场景等比缩放封面（接口须返回完整 URL）
 * @module shared/utils/ossImage
 */

/**
 * @typedef {'thumb' | 'bannerMain' | 'bannerSide' | 'detail' | 'original'} OssImagePreset
 */

/** @type {Record<OssImagePreset, { width: number, quality: number }|null>} */
const OSS_IMAGE_PRESETS = {
  /** 列表卡片封面（首页 / 分类 / 收藏 / 历史，展示宽约 150px） */
  thumb: { width: 300, quality: 80 },
  /** Banner 主焦点格 / H5 全宽图 */
  bannerMain: { width: 800, quality: 85 },
  /** Banner 侧格与预览裁切区 */
  bannerSide: { width: 300, quality: 80 },
  /** 漫画详情页封面（较大展示） */
  detail: { width: 600, quality: 85 },
  /** 原图，不追加处理参数 */
  original: null,
}

/** 支持 OSS 图片处理的域名 */
const OSS_PROCESSABLE_HOST_PATTERN = /\.aliyuncs\.com$/i

/**
 * 规范化图片 URL（仅 trim，不拼接域名；相对路径视为无效）
 * @param {string|null|undefined} url
 * @returns {string}
 */
export function normalizeImageUrl(url) {
  if (!url || typeof url !== 'string') return ''
  const trimmed = url.trim()
  if (!trimmed) return ''
  if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://')) {
    return ''
  }
  return trimmed
}

/**
 * 是否可对 URL 追加 x-oss-process
 * @param {string} url
 * @returns {boolean}
 */
function isOssProcessableUrl(url) {
  if (!url || url.includes('x-oss-process=')) return false
  try {
    const { hostname } = new URL(url)
    return OSS_PROCESSABLE_HOST_PATTERN.test(hostname)
  } catch {
    return false
  }
}

/**
 * 按宽度与质量拼接 OSS 图片处理参数（等比缩放）
 * @param {string} url 接口返回的完整 URL
 * @param {{ width: number, quality?: number }} options
 * @returns {string}
 */
export function buildOssImageUrl(url, options) {
  const normalized = normalizeImageUrl(url)
  if (!normalized) return ''
  if (!options?.width || !isOssProcessableUrl(normalized)) {
    return normalized
  }

  const quality = options.quality ?? 80
  const separator = normalized.includes('?') ? '&' : '?'
  return `${normalized}${separator}x-oss-process=image/resize,w_${options.width}/format,webp/quality,q_${quality}`
}

/**
 * 按预设获取 OSS 封面 URL
 * @param {string|null|undefined} url 接口返回的完整 URL
 * @param {OssImagePreset} [preset='thumb']
 * @returns {string}
 */
export function getOssImageUrl(url, preset = 'thumb') {
  const config = OSS_IMAGE_PRESETS[preset]
  if (!config) return normalizeImageUrl(url)
  return buildOssImageUrl(url, config)
}
